import React, { FC, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'antd';
import { Button, Input, Select } from '@cloudcar-app/cloudcar-components';
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import operations from 'apollo/operations';
import { AdminUpdateUserInput, User } from 'apollo/types';
import dealershipOptions from 'utils/kaufmann/dealershipMapper';
import { trimFormValues } from 'utils/trimFormValues';
import styles from './EditModal.module.scss';

interface UserModalProps {
  visible: boolean;
  handleOk: () => void;
  handleCancel: () => void;
  userData?: User | null;
  refetchUsers: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<
    ApolloQueryResult<{
      listUser: User[];
    }>
  >;
}

const UpdateUserModal: FC<UserModalProps> = (props: UserModalProps) => {
  const { visible, handleOk, handleCancel, userData, refetchUsers } = props;
  const [form] = Form.useForm();

  const [concessionaireAdminUpdateUser, { loading: loadingAdminUpdate }] =
    useMutation<{
      updateUserInput: AdminUpdateUserInput;
      adminUpdateUser: string;
    }>(operations.CONCESSIONAIRE_ADMIN_UPDATE_USER);

  const onFinish = async (formValues: Record<string, string>) => {
    const values = trimFormValues(formValues);
    await concessionaireAdminUpdateUser({
      variables: {
        updateUserInput: {
          username: userData?.username,
          bciUsername: values.bciUsername,
          bciDealer: values.bciDealer,
          bciPassword: values.bciPassword,
          zurichPassword: values.zurichPassword,
          dealer: values.dealer,
          role: values.role,
        },
        refetchQueries: [
          {
            query: operations.GET_ALL_USERS,
          },
        ],
      },
    });
    await refetchUsers();
    handleOk();
  };

  const onCancel = () => {
    handleCancel();
  };

  useEffect(() => {
    if (userData) {
      form.setFieldsValue({
        name: userData.name,
        rut: userData.username,
        bciUsername: userData.bciUsername,
        bciDealer: userData.bciDealer,
        bciPassword: userData.bciPassword,
        zurichPassword: userData.zurichPassword,
        dealer: userData.dealer,
        role: userData.role,
      });
    }
  }, [userData]);

  return (
    <>
      <Modal
        visible={visible}
        onOk={handleOk}
        onCancel={onCancel}
        footer={null}
        width="40%"
        bodyStyle={{ padding: '36px 36px 24px 36px' }}
      >
        <Form form={form} onFinish={onFinish}>
          <Row gutter={[16, 24]}>
            <Col span={24}>
              <Row justify="start">
                <h1>Editar usuario</h1>
              </Row>
            </Col>
            <Col span={24}>
              <Row justify="space-between" gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item name="name" className={styles.FormItem}>
                    <Input
                      placeholder="Nombre"
                      disabled
                      value={userData?.name}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="rut" className={styles.FormItem}>
                    <Input
                      placeholder="RUT"
                      disabled
                      value={userData?.username}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="bciUsername" className={styles.FormItem}>
                    <Input placeholder="Usuario BCI" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="bciDealer" className={styles.FormItem}>
                    <Input placeholder="Dealer BCI" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="bciPassword" className={styles.FormItem}>
                    <Input placeholder="Contraseña BCI" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="zurichPassword" className={styles.FormItem}>
                    <Input placeholder="Contraseña Zurich" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="dealer" className={styles.FormItem}>
                    <Select
                      compact
                      label="Sucursal"
                      placeholder="Sucursal"
                      options={dealershipOptions.map((option) => ({
                        label: option.label,
                        value: option.value,
                      }))}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="role" className={styles.FormItem}>
                    <Select
                      compact
                      label="Rol"
                      placeholder="Rol"
                      options={[
                        {
                          label: 'super-admin',
                          value: 'super-admin',
                        },
                        {
                          label: 'usuario-normal',
                          value: 'usuario-normal',
                        },
                        {
                          label: 'usuario-seguros',
                          value: 'usuario-seguros',
                        },
                      ].map((option) => ({
                        label: option.label,
                        value: option.value,
                      }))}
                      required
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row justify="end" gutter={8}>
                <Col>
                  <Button mode="secondary" size="large" onClick={onCancel}>
                    Cancelar
                  </Button>
                </Col>
                <Col>
                  <Button
                    htmlType="submit"
                    size="large"
                    loading={loadingAdminUpdate}
                  >
                    Guardar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

UpdateUserModal.defaultProps = {
  userData: undefined,
};

export default UpdateUserModal;
